import { AlertCircle, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * OverageDialog — shown when the workspace has hit its monthly proposal cap
 * and the server returned 402 { error: "monthly_proposal_cap_reached" }.
 *
 * Props:
 *   open        boolean — dialog visibility
 *   detail      the server's response.data.detail payload
 *                  { cap, drafted, overage_cents, message }
 *   onCancel    close without accepting
 *   onConfirm   user accepted the overage; caller must re-fire the request
 *                  with { accept_overage: true }
 *   busy        boolean — disables the CTA while the retry request is in-flight
 */
export default function OverageDialog({ open, detail, onCancel, onConfirm, busy = false }) {
  if (!open || !detail) return null;

  const dollars = ((detail.overage_cents || 0) / 100).toFixed(2);
  const cap = detail.cap;
  const drafted = detail.drafted;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      data-testid="overage-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden isolate" style={{ backgroundColor: "#ffffff" }}>
        <div className="px-6 pt-5 pb-4 border-b border-slate-200 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono font-bold tracking-widest text-amber-600">MONTHLY CAP REACHED</div>
            <div className="text-lg font-display font-bold text-slate-900 leading-tight mt-0.5">
              Continue as an overage?
            </div>
          </div>
          <button
            onClick={onCancel}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors"
            data-testid="overage-dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="text-sm text-slate-700 leading-relaxed" data-testid="overage-dialog-message">
            {detail.message || `You've used ${drafted} of ${cap} proposals this month.`}
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">USED</div>
              <div className="text-lg font-display font-bold text-slate-900" data-testid="overage-dialog-used">
                {drafted} / {cap}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500">OVERAGE FEE</div>
              <div className="text-lg font-display font-bold text-amber-700" data-testid="overage-dialog-fee">
                ${dollars}
              </div>
            </div>
          </div>

          <div className="text-xs text-slate-500">
            Overages are billed with your next invoice.{" "}
            <Link
              to="/pricing?from=starter&required=professional&reason=monthly_cap_reached"
              className="font-semibold text-violet-600 hover:text-violet-700 underline underline-offset-2"
              data-testid="overage-dialog-upgrade"
              onClick={onCancel}
            >
              Upgrade to Professional
            </Link>{" "}
            for 40 proposals/mo and a $15 overage rate.
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between gap-3">
          <button
            onClick={onCancel}
            className="text-sm font-mono font-bold tracking-widest text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="overage-dialog-cancel"
          >
            CANCEL
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className={`px-4 py-2 rounded-full text-sm font-mono font-bold tracking-widest text-white transition-all ${
              busy
                ? "bg-slate-400 cursor-not-allowed"
                : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-md"
            }`}
            data-testid="overage-dialog-confirm"
          >
            {busy ? "GENERATING…" : `CONTINUE · $${dollars}`}
          </button>
        </div>
      </div>
    </div>
  );
}
